// Diplomacy ribbon (top right): the two other playable leaders, then the leader's key relations
// (wars, denounced, allies) as medallions ringed in relation colour. Names are short forms
// (names.ts); the full name is in the tooltip and the accessible label. Non-state parties and
// blocs wear a glyph instead of a code. Hover: who holds office at the date, the relation's
// reason + sources. Click: the diplomacy screen for that nation.

import type { Services } from '../services/api.ts';
import type { Relation, RelationInfo } from '../world/api.ts';
import { RELATION_RANK } from './color.ts';
import { useServices } from './context.ts';
import { fmtDate } from './format.ts';
import { shortName } from './names.ts';
import { actorEmblem, actorKind, actorLook, emblemOf, lookOf, reportOf, selfIso, topLeader, type NationLook } from './nation.ts';
import { Medallion, RelationTag, relationIcon, useTip } from './parts.tsx';

const RIVAL_SIZE = 60;
const OTHER_SIZE = 44;
const OTHERS_MAX = 7;

// How many of each relation the ribbon shows; wars always come first.
const PER_RELATION: Partial<Record<Relation, number>> = { war: 4, denounced: 3, ally: 3 };

interface RibbonEntry {
  look: NationLook;
  info: RelationInfo;
  rival: boolean;
}

export function DiplomacyRibbon() {
  const services = useServices();
  const entries = ribbonEntries(services);
  if (entries.length === 0) {
    return null;
  }

  const rivals = entries.filter((e) => e.rival);
  const others = entries.filter((e) => !e.rival);
  return (
    <div class="wc-ribbon wc-live">
      <div class="wc-ribbon-plate" />
      {rivals.map((e) => (
        <RibbonMedal key={e.look.iso3} entry={e} size={RIVAL_SIZE} />
      ))}
      {others.length > 0 ? <span class="wc-ribbon-div" /> : null}
      {others.map((e) => (
        <RibbonMedal key={e.look.iso3} entry={e} size={OTHER_SIZE} />
      ))}
    </div>
  );
}

function ribbonEntries(services: Services): RibbonEntry[] {
  const { state, world } = services;
  const snap = state.snapshot.value;
  const self = selfIso(services);
  if (!snap || !self) {
    return [];
  }

  // The two other playable leaders, always first.
  const rivals: RibbonEntry[] = [];
  for (const p of world.perspectives()) {
    if (p.iso3 === self) {
      continue;
    }
    const report = reportOf(world, p.iso3);
    if (!report) {
      continue;
    }
    rivals.push({ look: lookOf(world, p.iso3), info: report.relation, rival: true });
  }
  const shown = new Set([self, ...rivals.map((r) => r.look.iso3)]);

  // Key relations, largest economies first within each relation.
  const econRank = new Map<string, number>();
  world.victory('economic').forEach((e, i) => econRank.set(e.iso3, i));

  const candidates: RibbonEntry[] = [];
  for (const [index, info] of snap.relations) {
    if (PER_RELATION[info.relation] === undefined) {
      continue;
    }
    const look = actorLook(world, index);
    if (!look || shown.has(look.iso3)) {
      continue;
    }
    candidates.push({ look, info, rival: false });
  }
  candidates.sort((a, b) => {
    const byRel = RELATION_RANK[a.info.relation] - RELATION_RANK[b.info.relation];
    if (byRel !== 0) {
      return byRel;
    }
    return (econRank.get(a.look.iso3) ?? Infinity) - (econRank.get(b.look.iso3) ?? Infinity);
  });

  const taken = new Map<Relation, number>();
  const others: RibbonEntry[] = [];
  for (const c of candidates) {
    const n = taken.get(c.info.relation) ?? 0;
    if (n >= (PER_RELATION[c.info.relation] ?? 0) || others.length >= OTHERS_MAX) {
      continue;
    }
    taken.set(c.info.relation, n + 1);
    others.push(c);
  }
  return [...rivals, ...others];
}

function RibbonMedal({ entry, size }: { entry: RibbonEntry; size: number }) {
  const { world, actions } = useServices();
  const { look, info, rival } = entry;
  const handlers = useTip(() => <RelationTip entry={entry} />, 'below');
  const emblem = rival ? emblemOf(world, look.iso3, 'person') : actorEmblem(world, look.iso3);

  return (
    <button
      type="button"
      class={rival ? 'wc-ribbon-medal wc-ribbon-medal--rival' : 'wc-ribbon-medal'}
      aria-label={`${look.name}: ${info.relation}`}
      onClick={() => actions.openPanel('diplomacy', look.iso3)}
      {...handlers}
    >
      <Medallion {...emblem} size={size} relation={info.relation} badge={relationIcon(info.relation)} />
      <span class="wc-ribbon-name">{shortName(look.name, actorKind(world, look.iso3) ?? 'country')}</span>
    </button>
  );
}

function RelationTip({ entry }: { entry: RibbonEntry }) {
  const { world } = useServices();
  const { look, info } = entry;
  const holder = topLeader(world, look.iso3);
  return (
    <>
      <div class="wc-tip-title">{look.name}</div>
      {holder ? (
        <div class="wc-tip-note">
          {holder.name}, since {fmtDate(holder.since)}
        </div>
      ) : null}
      <RelationTag relation={info.relation} />
      <div class="wc-tip-reason">{info.reason}</div>
      {info.sources.slice(0, 3).map((id) => (
        <div class="wc-tip-src" key={id}>
          <b>{world.source(id)?.publisher ?? id}</b>
          <span>{world.source(id)?.title ?? ''}</span>
        </div>
      ))}
      <div class="wc-tip-hint">Click for diplomacy</div>
    </>
  );
}
